import { cn } from "@newxsystems/shared";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;
const washes = ["bg-lilac", "bg-melon", "bg-sky", "bg-mint"] as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-8 md:gap-10">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid grid-cols-1 gap-5 sm:grid-cols-2", columns[columnCount || 3])}>
          {items.map((item, index) => (
            <DaylightCardLink
              className={cn(
                "flex flex-col rounded-[28px] p-7 text-ink transition-transform duration-300 ease-out hover:-translate-y-1 md:p-8",
                washes[index % washes.length],
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <div className="flex min-h-14 items-center gap-4">
                {item.portrait ? (
                  <DaylightMedia
                    aspectRatio="1:1"
                    className="size-14 shrink-0"
                    media={item.portrait.image}
                    sizes="56px"
                  />
                ) : null}
                <div>
                  <p className="font-daylight-mono text-sm font-medium">{item.name}</p>
                  {item.attribution ? (
                    <p className="mt-1 font-daylight-body text-sm text-slate">{item.attribution}</p>
                  ) : null}
                </div>
              </div>
              <blockquote className="mt-8 border-t border-ink/15 pt-7">
                <DaylightRichText
                  className="font-daylight-body text-2xl leading-snug text-ink md:text-3xl"
                  content={item.quote}
                />
              </blockquote>
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
